/**
 * Encounters (Screen I) — a READABLE, honest, member-keyed INTERNAL-visit history over op_fact_encounter
 * (ambulatory, REAL). The reusable ParticipantPicker selects a member (defaulting to the encounters
 * exemplar — the visit-richest REAL member); the screen binds GET /participants/:key/encounters and:
 *   1. leads with the true cross-page count from meta.total ("805 internal encounters"), then groups the
 *      CURRENT page (date-DESC) by YEAR via the pure groupByYear helper — each year a compact timeline of
 *      date • AMB class chip • CPT label • a telehealth chip when is_telehealth_flag is set;
 *   2. labels the visit code from the STANDARD AMA CPT reference (cptLabel) — known -> "{code} — {label}",
 *      unknown -> bare code, null -> an honest "—" (no CPT recorded), with a footnote that the meaning is
 *      the standard AMA code, NOT from op_*;
 *   3. is honest about what is DEFERRED: provider name, encounter type, location, place-of-service and
 *      LOS/cost are not yet in the operational DB (no op_dim_provider · dim_encounter · dim_location
 *      feeds) — surfaced as a caveat, never blank-as-absence-of-care.
 * READ-ONLY (no authoring — scribe is deferred); external ED/IP/SNF utilization is a SEPARATE mock screen
 * and is NOT shown or conflated here. An empty result shows the provenance badge, never a silent zero.
 * Practitioner name is never console.logged (provider PHI) — and is not surfaced here at all (DEFERRED).
 */
import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  SkyCard,
  SkyText,
  SkyBadge,
  SkyTooltip,
  SkyLoader,
  SkyMessageBar,
} from "@skypointcloud/skypoint-ui";
import { CalendarDays, Video, Info, Stethoscope } from "lucide-react";
import type { Encounter } from "@shared/contracts";
import { usePage } from "../lib/usePage";
import { encounterListSchema } from "../lib/schemas/member";
import { useExemplarMember } from "../lib/useExemplarMember";
import { groupByYear } from "../lib/encounterTimeline";
import { cptLabel, cptDescription, NO_CPT, CPT_REFERENCE_NOTE } from "../lib/cptLabels";
import { ParticipantPicker } from "../components/ParticipantPicker";
import { ProvenanceBadge } from "../provenance/ProvenanceBadge";
import { PageHeader } from "../shell/PageHeader";
import { PageFooter } from "../shell/PageFooter";

// What is still DEFERRED for an encounter — surfaced honestly so a blank cell never reads as "no care
// happened". Provider name + NPI now resolve via gold.dim_practitioner (REAL); the rest await their dims.
const DEFERRED_FIELDS =
  "Encounter type (dim_encounter), location and place-of-service (dim_location), and length-of-stay / " +
  "cost are not yet in the operational DB. They render blank by absence-of-feed, not absence-of-care.";

/** A provider name from the gold dim, or an honest placeholder when no practitioner resolved. */
function providerName(row: Encounter): string {
  const name = [row.practitioner_first_name, row.practitioner_last_name]
    .map((s) => s?.trim())
    .filter(Boolean)
    .join(" ");
  return name || "Not recorded";
}

/** One CPT cell: known codes get a tooltip flagging the standard-AMA caveat; null -> honest placeholder. */
function CptCell({ code }: { code: string | null }) {
  const label = cptLabel(code);
  if (label === NO_CPT) {
    return (
      <SkyText size={200} color="muted" font="numeric">
        {NO_CPT} no CPT recorded
      </SkyText>
    );
  }
  const isStandard = cptDescription(code) !== null;
  const text = (
    <SkyText size={200} font="numeric">
      {label}
    </SkyText>
  );
  return isStandard ? <SkyTooltip content={CPT_REFERENCE_NOTE} side="top">{text}</SkyTooltip> : text;
}

/** A single encounter row in the year timeline. A small responsive grid keeps the date-led timeline
 *  columnar: date | class | provider on the first row; the CPT cell (+ telehealth chip) reflows onto a
 *  second line under md so a narrow content column never scrambles the left-aligned date/class. */
function TimelineRow({ row }: { row: Encounter }) {
  const provider = providerName(row);
  return (
    <li className="grid grid-cols-1 items-center gap-x-3 gap-y-1 py-2 md:grid-cols-[7rem_auto_1fr_auto]">
      <span className="flex items-center gap-1.5">
        <CalendarDays className="size-3.5 text-neutral-foreground-3" aria-hidden />
        <SkyText size={300} font="numeric">
          {row.period_start ?? "Date not recorded"}
        </SkyText>
      </span>
      <span className="flex items-center">
        <SkyBadge color="informative" appearance="tint" size="small" aria-label="Ambulatory encounter">
          {row.encounter_class ?? "Unknown class"}
        </SkyBadge>
      </span>
      {/* Provider (gold.dim_practitioner) — was DEFERRED, now REAL. Honest "Not recorded" when no match. */}
      <span className="flex min-w-0 items-center gap-1.5">
        <Stethoscope className="size-3.5 shrink-0 text-neutral-foreground-3" aria-hidden />
        <SkyText size={300} color={provider === "Not recorded" ? "muted" : undefined}>
          {provider}
        </SkyText>
        {row.practitioner_is_pcp && (
          <SkyBadge color="important" appearance="tint" size="small" aria-label="Primary care provider">
            PCP
          </SkyBadge>
        )}
      </span>
      {/* CPT + telehealth: their own trailing column on md, stacked onto its own line on narrow. */}
      <span className="flex min-w-0 items-center gap-2 md:justify-end">
        <CptCell code={row.cpt_or_hcpcs_code} />
        {row.is_telehealth_flag && (
          <SkyBadge color="success" appearance="tint" size="small" icon={Video} aria-label="Telehealth visit">
            Telehealth
          </SkyBadge>
        )}
      </span>
    </li>
  );
}

export function Encounters() {
  // Manual pick wins; else a ?member= deep-link (from the Member 360 "view all encounters" link); else
  // this section's exemplar (the encounters-richest REAL member) so the screen never defaults to an
  // empty member. The ParticipantPicker still lets the user change member.
  const [picked, setPicked] = useState<string | null>(null);
  const [params] = useSearchParams();
  const memberParam = params.get("member");
  const validParam = memberParam && /^-?\d+$/.test(memberParam) ? memberParam : null;
  const { key: defaultKey, error: exemplarError } = useExemplarMember("encounters");
  const key = picked ?? validParam ?? defaultKey;

  // pageSize=100 pulls a deep slice of the history so the year timeline is substantial in one fetch; the
  // TRUE cross-page total comes from meta.total (shown prominently), not from the grouped page length.
  const { data, meta, loading, error } = usePage<Encounter[]>(
    key ? `/api/v1/participants/${key}/encounters?pageSize=100` : null,
    encounterListSchema,
  );

  // Array.isArray, not `?? []`: a non-array truthy payload would make groupByYear's iteration throw
  // "not iterable" during render. Guard the shape so a drifted envelope degrades to empty, not a crash.
  const groups = useMemo(() => groupByYear(Array.isArray(data) ? data : []), [data]);
  const total = meta?.total;
  const pageCount = data?.length ?? 0;
  const hasData = pageCount > 0;

  return (
    <>
      <PageHeader
        crumbs={[{ key: "home", label: "skyCare" }, { key: "encounters", label: "Encounters" }]}
        title="Encounters"
        description="Internal visit history read live from op_fact_encounter (ambulatory). Read-only; external ED/IP/SNF utilization is not shown here (mock, surfaced separately)."
      />
      <section className="mt-6 flex flex-1 flex-col gap-4">
        <div className="flex items-end justify-between gap-4">
          <div className="w-80">
            <ParticipantPicker value={key ?? undefined} onSelect={setPicked} />
          </div>
          {key && <ProvenanceBadge meta={meta?.provenance} />}
        </div>

        {/* The default-member lookup failed and the user has not picked one yet: surface an honest bar
            (the picker above is the way out) instead of a silent blank screen. */}
        {exemplarError && !key && (
          <SkyMessageBar
            intent="warning"
            title="Could not load a default member"
            message="Could not load a default member - search above to choose a participant."
          />
        )}

        {loading && <SkyLoader label="Loading encounters" />}
        {error && <SkyMessageBar intent="error" title="Could not load encounters" message={error} />}

        {key && !loading && !error && (
          <>
            {/* True cross-page count from meta.total — honest "empty, not zero" when there is no data. */}
            <div className="flex flex-wrap items-baseline gap-2">
              <SkyText size={600} weight="bold" font="numeric">
                {typeof total === "number" ? total.toLocaleString() : hasData ? pageCount : "Not recorded"}
              </SkyText>
              <SkyText size={300} color="muted">
                internal encounters (ambulatory)
              </SkyText>
            </div>

            {/* Honest DEFERRED caveat — never let a blank cell read as absence of care. */}
            <SkyMessageBar
              intent="info"
              title="Provider now resolved; some encounter detail is still deferred"
              message={DEFERRED_FIELDS}
            />

            {!hasData && (
              <SkyCard appearance="outline">
                <div className="flex flex-col gap-1 py-2">
                  <SkyText size={300} weight="semibold">
                    Awaiting feed
                  </SkyText>
                  <SkyText size={200} color="muted">
                    No internal encounters on record for this member yet. This is a provenance-badged empty
                    state, not a zero.
                  </SkyText>
                </div>
              </SkyCard>
            )}

            {hasData && (
              <>
                {typeof total === "number" && total > pageCount && (
                  <SkyText size={200} color="muted">
                    Showing the most recent {pageCount.toLocaleString()} of {total.toLocaleString()},
                    grouped by year.
                  </SkyText>
                )}
                {groups.map((group) => (
                  <SkyCard
                    key={group.year}
                    appearance="outline"
                    header={{
                      title: group.year,
                      action: (
                        <SkyBadge color="subtle" appearance="tint" size="small" aria-label={`${group.count} encounters in ${group.year}`}>
                          {group.count}
                        </SkyBadge>
                      ),
                    }}
                  >
                    <ul className="flex flex-col divide-y divide-neutral-stroke-2">
                      {group.rows.map((row, i) => (
                        <TimelineRow key={`${group.year}-${i}`} row={row} />
                      ))}
                    </ul>
                  </SkyCard>
                ))}

                {/* Standard-AMA CPT reference footnote (the one allowed external lookup). */}
                <div className="flex items-center gap-1.5">
                  <Info className="size-3.5 text-neutral-foreground-3" aria-hidden />
                  <SkyText size={200} color="muted">
                    {CPT_REFERENCE_NOTE}
                  </SkyText>
                </div>
              </>
            )}
          </>
        )}
      </section>
      <PageFooter />
    </>
  );
}
